import {
	Component, Input, ViewChild, OnDestroy, AfterViewInit, ChangeDetectionStrategy,
	ChangeDetectorRef

} from '@angular/core';
import { Subscription } from 'rxjs/Rx';
import { DiscResultsService } from 'discovery';
import {
	Owner,
	ViewerDeviceGroupContainerGUIDs,
	ViewerDeviceGroupDto
} from 'UvExplorerServer.Dtos.TS/Viewer';
import {
	DisplayDeviceGroupTreeDto, DisplayDeviceGroupContainerDto, DisplayDeviceGroupDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { DeviceListComponent } from '../devicelist/devicelist.component';
import { Layer2MapComponent } from 'topomap';
import { NetFacadeService, NavigationService } from 'netview';
import { IdentityService } from 'identity';
import { MessageService } from 'base';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { MenuItem, TreeNode } from 'primeng/api';
import { DeviceGroup } from '../../../DiscoveryEngine.TS/Core';

const customGroupsContainerName: string = "Custom Groups";

@Component({
	selector: 'devices',
	templateUrl: './devices.component.html',
	styleUrls: ['./devices.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DevicesComponent implements OnDestroy, AfterViewInit {

	private static readonly DeviceListTabIndex = 1;
	private static readonly MapTabIndex = 2;

	@Input()
	public selected: boolean;

	private deviceGroupTree: DisplayDeviceGroupTreeDto;
	public selectedDeviceGroup: DisplayDeviceGroupDto | null;
	public selectedViewerDeviceGroup: ViewerDeviceGroupDto | null;
	public items: MenuItem[];
	public customGroupsItems: MenuItem[];
	public treeNodes: TreeNode[];
	public selectedTreeNode: TreeNode | null;

	@ViewChild(DeviceListComponent)
	public deviceList: DeviceListComponent;

	@ViewChild(Layer2MapComponent)
	public layer2Map: Layer2MapComponent;

	private deviceGroupTreeLoadedSub: Subscription;
	private deviceGroupTreeChangedSub: Subscription;
	private locationChangedSub: Subscription;

	public deviceGroupDialogVisible: boolean;
	public displayDeleteChildrenConfirm: boolean;
	public network: NetViewDto;

	// variables for handling group-name click logic
	private clickWaiting: boolean;
	private clickDeviceGroup: DisplayDeviceGroupDto;
	private clickIsDouble: boolean;

	public active: number = 1;

	public loading: boolean = false;

	constructor(private discResultsService: DiscResultsService,
		private netFacadeService: NetFacadeService,
		private changeDetector: ChangeDetectorRef,
		private navService: NavigationService,
		private identityService: IdentityService,
		private messageService: MessageService) {

		this.deviceGroupTree = this.emptyDeviceGroupTree();

		this.deviceGroupTreeLoadedSub = this.discResultsService.onDeviceGroupTreeLoaded.subscribe(
			(tree: DisplayDeviceGroupTreeDto) => {
				this.setDeviceGroupTree(tree);
			},
			(error?) => {
				this.setDeviceGroupTree(null);
			});
		this.deviceGroupTreeChangedSub = this.discResultsService.onDeviceGroupTreeChanged.subscribe(
			() => {
				this.setDeviceGroupTree(this.discResultsService.deviceGroupTree);
			},
			(error?) => {
				this.setDeviceGroupTree(this.discResultsService.deviceGroupTree);
			});

		this.discResultsService.selectedDeviceGroupChanged.subscribe((deviceGroupGuid) => {
			let group = this.deviceGroupTree.FindGroupByTagGuid(deviceGroupGuid);
			if (group != null) {
				this.setSelectedDeviceGroup(group);
			}
		});

		this.selected = false;
		this.deviceList = null;
		this.layer2Map = null;

		this.deviceGroupDialogVisible = false;
		this.displayDeleteChildrenConfirm = false;
		this.network = null;

		this.selectedDeviceGroup = null;
		this.selectedViewerDeviceGroup = null;
		this.treeNodes = [];
		this.selectedTreeNode = null;

		this.clickDeviceGroup = null;
		this.clickIsDouble = false;
		this.clickWaiting = false;

		this.locationChangedSub = this.navService.onLocationChanged.subscribe(
			() => {
				this.updateNetwork();
			},
			(error) => {
				this.updateNetwork();
			});
		this.updateNetwork();

		this.items = [
			{ label: 'Edit Group...', command: (event) => this.editDeviceGroup() },
			{ label: 'Export Device Data', command: (event) => this.exportDeviceData() }
		];

		this.customGroupsItems = [
			{ label: 'Edit Group...', command: (event) => this.editDeviceGroup() },
			{ label: 'Create Child Group...', command: (event) => this.createNewDeviceGroup(this.selectedDeviceGroup?.VdgGuid ?? null, null, false) },
			{ label: 'Delete Group...', command: (event) => this.tryDeleteChildren() },
			{ label: 'Export Device Data', command: (event) => this.exportDeviceData() }
		];

		this.resetClickState();
	}

	public reloadDeviceList(): void {
		this.deviceList.reloadDeviceList();
	}

	private get userGroupsContainer(): DisplayDeviceGroupContainerDto {
		if (this.deviceGroupTree) {
			return this.deviceGroupTree.UserGroupsContainer;
		}
		else {
			return null;
		}
	}

	private updateNetwork(): void {
		let netViewGuid = this.navService.location.netViewGuid;
		if (netViewGuid) {
			this.network = this.netFacadeService.findNetwork(netViewGuid);
		}
		else {
			this.network = null;
		}
	}

	private resetClickState(): void {
		this.clickWaiting = false;
		this.clickDeviceGroup = undefined;
		this.clickIsDouble = false;
	}

	public onGroupNameClick(group: DisplayDeviceGroupDto): void {
		let DoubleClickDelay = 500; // ms

		if (this.clickWaiting && group == this.clickDeviceGroup) {
			this.clickIsDouble = true;
			return;
		}

		this.clickWaiting = true;
		this.clickDeviceGroup = group;
		this.clickIsDouble = false;
		setTimeout(() => {
			let isDoubleClick = this.clickIsDouble;
			this.resetClickState();

			if (group != this.selectedDeviceGroup) {
				this.setSelectedDeviceGroup(group, (result: boolean) => {
					if (result && isDoubleClick) {
						this.editDeviceGroup();
					}
				});
			}
			else if (isDoubleClick) {
				this.editDeviceGroup();
			}
		}, DoubleClickDelay);
	}

	private get sessionGuid(): string {
		return this.identityService.sessionGuid;
	}

	private get netViewGuid(): string {
		return this.navService.location.netViewGuid;
	}

	private emptyDeviceGroupTree(): DisplayDeviceGroupTreeDto {

		let container = new DisplayDeviceGroupContainerDto();
		container.Name = "Loading...";

		let rootContainer = new DisplayDeviceGroupContainerDto();
		rootContainer.Containers.Add(container);

		let tree = new DisplayDeviceGroupTreeDto();
		tree.RootContainer = rootContainer;

		return tree;
	}

	private setDeviceGroupTree(tree: DisplayDeviceGroupTreeDto | null): void {
		this.deviceGroupTree = tree ? tree : this.emptyDeviceGroupTree();
		this.rebuildCustomGroupTree();
		this.restoreGroupSelection();
		this.changeDetector.markForCheck();
	}

	public ngOnDestroy(): void {
		if (this.deviceGroupTreeLoadedSub) {
			this.deviceGroupTreeLoadedSub.unsubscribe();
			this.deviceGroupTreeLoadedSub = undefined;
		}
		if (this.deviceGroupTreeChangedSub) {
			this.deviceGroupTreeChangedSub.unsubscribe();
			this.deviceGroupTreeChangedSub = undefined;
		}
		if (this.locationChangedSub) {
			this.locationChangedSub.unsubscribe();
			this.locationChangedSub = undefined;
		}
	}

	public showDeviceOnMap(deviceGuid: string): void {
		this.active = DevicesComponent.MapTabIndex;
		this.layer2Map?.showDeviceOnMap(deviceGuid);
	}

	public showDeviceGroupOnMap(deviceGroupGuid: string): void {
		this.active = DevicesComponent.MapTabIndex;
		this.layer2Map?.showDeviceOnMap(deviceGroupGuid);
	}

	public onTabChange(event): void {
		this.layer2Map?.setVisible(event.nextId == DevicesComponent.MapTabIndex);
		this.deviceList.setVisible(event.nextId == DevicesComponent.DeviceListTabIndex);
	}

	private isRegularDeviceGroup(ddg: DisplayDeviceGroupDto): boolean {
		return !!ddg.VdgTagGuid;
	}

	public groupDisplayName(ddg: DisplayDeviceGroupDto): string {
		if (this.isRegularDeviceGroup(ddg)) {
			// Regular device group
			return ddg.Name + " (" + ddg.DeviceCount + ")";
		}
		else {
			// Generated device group (subnet or vlan)
			return ddg.Name;
		}
	}

	public editDeviceGroup(): void {
		let topGroupContainer = null;
		if (!!this.selectedDeviceGroup) {
			topGroupContainer = this.deviceGroupTree.FindTopContainerOfGroup(this.selectedDeviceGroup);
		}
		if (!!topGroupContainer && /*topGroupContainer == this.userGroupsContainer && */ this.isRegularDeviceGroup(this.selectedDeviceGroup)) {
			let guid = this.selectedDeviceGroup.VdgGuid;
			this.discResultsService.getDeviceGroup(this.sessionGuid, this.netViewGuid, guid).subscribe(
				(result: ViewerDeviceGroupDto) => {
					this.selectedViewerDeviceGroup = result;
					this.deviceGroupDialogVisible = true;
					this.changeDetector.markForCheck();
				},
				(error?) => {
					this.messageService.displayErrorMessage("Could not retrieve device group");
				});
		}
		else {
			this.messageService.displayErrorMessage("Cannot edit the currently selected device group");
		}
	}

	public tryDeleteChildren(): void {
		if (this.selectedDeviceGroup.HasChildGroups) {
			this.displayDeleteChildrenConfirm = true;
		}
		else {
			this.deleteDeviceGroup();
		}
	}

	public cancelDeleteChildren(): void {
		this.displayDeleteChildrenConfirm = false;
	}

	public deleteDeviceGroup(): void {
		let errorMessage = "Device group deletion failed";
		if (this.netViewGuid && this.userGroupsContainer) {
			let topGroupContainer = null;
			if (!!this.selectedDeviceGroup) {
				topGroupContainer = this.deviceGroupTree.FindTopContainerOfGroup(this.selectedDeviceGroup);
			}
			if (!!topGroupContainer && topGroupContainer == this.userGroupsContainer) {
				let parentGuid = this.deviceGroupTree.FindParentGuidOfGroup(this.selectedDeviceGroup);

				this.discResultsService.deleteDeviceGroup(this.sessionGuid, this.netViewGuid,
					this.selectedDeviceGroup.VdgGuid, parentGuid).subscribe(
						(result: boolean) => {
							//let removedGroup = this.removeGroupFromCustomGroupTree(this.selectedDeviceGroup);
							//this.treeGroups;
							this.rebuildCustomGroupTree();
							this.selectedDeviceGroup = null;
							this.selectedViewerDeviceGroup = null;
						},
						(error: any) => {
							this.messageService.displayErrorMessage(errorMessage);
						});
			}
			// We currently do not allow the user to delete the selected device group if it is not a custom group
			else {
				this.messageService.displayErrorMessage("Cannot delete the currently selected device group if it is not a custom group");
			}
		}
		else {
			console.error(errorMessage);
		}
		this.displayDeleteChildrenConfirm = false;
	}

	public createNewDeviceGroup(groupParentGuid: string, containerParentGuid: string, creatingNewTopGroup: boolean): void {
		let errorMessage = "Device group creation failed";

		if (!this.netViewGuid || !this.userGroupsContainer) {
			console.error(errorMessage);
			return;
		}

		let topGroupContainer = null;
		if (!!this.selectedDeviceGroup) {
			topGroupContainer = this.deviceGroupTree.FindTopContainerOfGroup(this.selectedDeviceGroup);
		}

		// We check if we're creating a new top group first, because if so there may not be a selectedDeviceGroup. 
		// We currently do not allow the user to create a child group of selected device group if it is not a custom group
		if (!this.IsTopLevelGroup(creatingNewTopGroup, topGroupContainer) && !this.selectedDeviceGroup) {
			this.messageService.displayErrorMessage("Cannot create new groups or child groups if currently selected group is not a custom group");
			return;
		}

		let vdg = this.createViewerDeviceGroupDto(this.netViewGuid, "New Device Group");
		if (vdg === null) console.error(errorMessage); // Should this short circuit here?
		this.discResultsService.createDeviceGroup(this.sessionGuid, vdg, groupParentGuid, containerParentGuid, -1).subscribe(
			(result: DisplayDeviceGroupDto) => {
				this.deviceGroupDialogVisible = true;
				this.selectedDeviceGroup = result;
				this.selectedViewerDeviceGroup = vdg;
				this.setDeviceGroupTree(this.discResultsService.deviceGroupTree);
				this.rebuildCustomGroupTree();
			},
			(error: any) => {
				this.messageService.displayErrorMessage(errorMessage);
			});
	}

	private IsTopLevelGroup(creatingNewTopGroup, topGroupContainer): boolean {
		return creatingNewTopGroup || (!!topGroupContainer && (topGroupContainer == this.userGroupsContainer))
	}

	private createViewerDeviceGroupDto(netViewGuid: string, name: string): ViewerDeviceGroupDto {
		let dg = new DeviceGroup();
		dg.VerifyGuid();
		dg.Name = name;

		let newVdg = new ViewerDeviceGroupDto();
		newVdg.ScopeGuid = netViewGuid;
		newVdg.Owner = Owner.User;
		newVdg.Image = "";
		newVdg.Name = name;
		newVdg.TagGuid = dg.GUID;
		newVdg.DeviceGroup = dg;

		return newVdg;
	}

	public rebuildCustomGroupTree(): void {
		this.treeNodes = [];

		let topLevelGroups: DisplayDeviceGroupDto[] = [];

		// Retrieve user groups from the current device group tree
		let _userGroupsContainer = this.userGroupsContainer;
		if (_userGroupsContainer) {
			topLevelGroups = _userGroupsContainer.Groups.ToArray();
			topLevelGroups.sort((a, b) => a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1);

			// Now build the custom group tree
			topLevelGroups.forEach((group, index) => {
				let treeNode = this.buildChildGroups(group);
				this.treeNodes.push(treeNode);
			});
		}
	}

	private buildChildGroups(group: DisplayDeviceGroupDto): TreeNode {
		const treeNode: TreeNode = {
			label: group.Name + " (" + group.DeviceCount + ")",
			data: group,
			children: [],
		};

		if (group.HasChildGroups) {
			// Recursively build children
			group.ChildGroups.ForEach((childGroup) => {
				let childNode = this.buildChildGroups(childGroup);
				treeNode.children!.push(childNode);
			});
		}

		return treeNode;
	}

	private setSelectedDeviceGroup(ddg: DisplayDeviceGroupDto, callback?: (devGroupLoaded: boolean) => void): void {

		this.selectedDeviceGroup = ddg;
		this.deviceList.setSelectedDeviceGroup(ddg);
		this.layer2Map?.setSelectedDeviceGroup(ddg);
		this.selectedTreeNode = null;

		if (ddg) {
			this.treeNodes.some((node: TreeNode) => {
				if (node.data.VdgGuid === ddg.VdgGuid) {
					this.selectedTreeNode = node;
					return true;
				}
				return false;
			});

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;
			let vdgGuid = ddg.VdgGuid;

			this.loading = true;
			this.discResultsService.loadDeviceGroup(sessionGuid, netViewGuid, agentGuid,
				schedDiscGuid, schedDiscRsltGuid, vdgGuid).subscribe(
					(result: boolean) => {
						this.loading = false;
						if (callback)
							callback(true);
					},
					(error) => {
						this.loading = false;
						if (callback)
							callback(false);
					});
		}

		this.changeDetector.markForCheck();
	}

	ngAfterViewInit() {
		// this.updateSelectedDeviceGroup();
	}

	// The device group tree has been updated, discarding the previously selected device group object.
	// Therefore, we need to update the selected group, preserving the prior selection if possible.
	private restoreGroupSelection(): void {
		let tree = this.deviceGroupTree;
		if (tree) {
			let newSelection: DisplayDeviceGroupDto = null;
			if (this.selectedDeviceGroup) {
				let selectedGuid: string = this.selectedDeviceGroup.VdgGuid;
				newSelection = tree.FindGroupByGuid(selectedGuid);
			}
			if (!newSelection &&
				tree.RootContainer.Containers.Count > 0 &&
				tree.RootContainer.Containers.Get(0).Groups.Count > 0) {
				newSelection = tree.RootContainer.Containers.Get(0).Groups.Get(0);
			}
			if (newSelection) {
				this.setSelectedDeviceGroup(newSelection);
			}
			else {
				this.setSelectedDeviceGroup(null);
			}
		}
		else {
			this.setSelectedDeviceGroup(null);
		}
	}

	public get topLevelContainers(): DisplayDeviceGroupContainerDto[] {
		return this.deviceGroupTree.RootContainer.Containers.ToArray();
	}

	public getContainerGroups(container: DisplayDeviceGroupContainerDto): DisplayDeviceGroupDto[] {
		let groups = container.Groups.ToArray();

		switch (container.VdgcTagGuid) {
			case ViewerDeviceGroupContainerGUIDs.UserGroups:
			case ViewerDeviceGroupContainerGUIDs.Vlans:
				groups.sort((a: DisplayDeviceGroupDto, b: DisplayDeviceGroupDto) => {
					return a.Name.localeCompare(b.Name);
				});
				break;
		}

		return groups;
	}

	private exportDeviceData(): void {
		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.messageService.startWork();
		this.discResultsService.exportDeviceData(sessionGuid, netViewGuid, agentGuid,
			schedDiscGuid, schedDiscRsltGuid).subscribe(
				(result: Blob) => {
					this.messageService.endWork();
					let filename = "devices.dis";
					this.download(result, filename, "application/octet-stream");
				},
				(error) => {
					this.messageService.endWork();
					this.messageService.displayErrorMessage("Device export failed");
				});
	}

	// Function to download data to a file
	private download(data, filename, type): void {
		var file;
		if (data instanceof Blob) {
			file = data;
		}
		else {
			file = new Blob([data], { type: type });
		}

		var a = document.createElement("a"),
			url = URL.createObjectURL(file);
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		setTimeout(function () {
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}, 0);

	}

}
